<template>
  <DashboardLayout pageTitle="Dashboard">
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div v-for="stat in stats" :key="stat.label" class="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex flex-col justify-between h-44 transition-all hover:shadow-md">
        <div class="flex justify-between items-start">
          <div>
            <p class="text-gray-500 text-sm font-medium">{{ stat.label }}</p>
            <h3 class="text-2xl font-bold mt-2 text-gray-900">
              {{ loading ? '...' : stat.value }}
            </h3>
          </div>
          <div :class="`p-2 rounded-lg ${stat.color}`">
            <span class="material-icons text-2xl">{{ stat.icon }}</span>
          </div>
        </div>
        <p :class="`text-sm font-medium ${stat.trendColor} flex items-center gap-1 mt-4`">
          <span class="material-icons text-sm">{{ stat.trendIcon }}</span>
          {{ stat.trendText }}
        </p>
      </div>
    </div>

    <div class="mb-8">
      <h4 class="text-base font-bold text-gray-800 mb-4">Quick Actions</h4>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <router-link 
          v-for="action in actions" 
          :key="action.label" 
          :to="{ name: action.routeName }"
          class="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm transition-all duration-200 group flex flex-col items-center gap-3 no-underline hover:-translate-y-1 hover:border-primary/20"
        >
          <span class="material-icons text-2xl text-gray-600 group-hover:text-primary transition-colors">
            {{ action.icon }}
          </span>
          <span class="text-sm font-bold text-gray-800 group-hover:text-primary transition-colors">
            {{ action.label }}
          </span>
        </router-link>
      </div>
    </div>

    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div class="p-6 flex justify-between items-center border-b border-gray-50">
        <h4 class="text-base font-bold text-gray-800">Recent Patient Records</h4>
        <router-link :to="{ name: 'Patients' }" class="text-[#3C64CF] font-bold text-sm hover:underline">
          View All
        </router-link>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="text-gray-500 text-sm bg-gray-50/50">
              <th class="px-6 py-4 font-semibold">Patient Name</th>
              <th class="px-6 py-4 font-semibold">Medication</th>
              <th class="px-6 py-4 font-semibold">Doctor</th>
              <th class="px-6 py-4 font-semibold text-right">Date</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50">
            <tr v-for="patient in patients" :key="patient.id" class="hover:bg-gray-50/50 transition cursor-pointer group">
              <td class="px-6 py-4">
                <span class="text-sm font-semibold text-gray-800">{{ patient.name }}</span>
              </td>
              <td class="px-6 py-4 text-gray-500 text-sm">
                {{ patient.medication || 'Not specified' }}
              </td>
              <td class="px-6 py-4 text-gray-600 font-medium text-sm">
                {{ patient.doctor || 'N/A' }}
              </td>
              <td class="px-6 py-4 text-right">
                <span class="text-xs font-bold text-gray-400">
                  {{ patient.date }}
                </span>
              </td>
            </tr>
            <tr v-if="patients.length === 0 && !loading">
              <td colspan="4" class="px-6 py-10 text-center text-gray-400 text-sm italic">
                No recent records found.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </DashboardLayout>
</template>

<script>
import DashboardLayout from './layouts/DashboardLayout.vue'
import api from '@/services/api'

export default {
  name: 'StaffDashboard',
  components: { DashboardLayout },
  data() {
    return {
      loading: true,
      stats: [
        { 
          key: 'today_submissions',
          label: "Today's Submission", value: "0", icon: "upload", 
          color: "bg-blue-50 text-blue-600", trendText: "Live status", 
          trendIcon: "schedule", trendColor: "text-blue-500" 
        },
        { 
          key: 'total_patients',
          label: "Total Patients", value: "0", icon: "group", 
          color: "bg-blue-50 text-blue-600", trendText: "User role", 
          trendIcon: "trending_up", trendColor: "text-green-500" 
        },
        { 
          key: 'total_staff',
          label: "Total Staff", value: "0", icon: "badge", 
          color: "bg-blue-50 text-blue-600", trendText: "Staff/Admin role", 
          trendIcon: "verified", trendColor: "text-blue-500" 
        },
        { 
          key: 'weekly_reports',
          label: "Weekly Reports", value: "0", icon: "assessment", 
          color: "bg-blue-50 text-blue-600", trendText: "Current week", 
          trendIcon: "check_circle", trendColor: "text-gray-400" 
        },
      ],
      actions: [
        { label: "Register Patient", icon: "person_add", routeName: "Patients" },
        { label: "Manage Staff", icon: "assignment_ind", routeName: "Staff" },
        { label: "Analytics", icon: "description", routeName: "Analytics" },
      ],
      patients: [] 
    }
  },
  methods: {
    async fetchDashboardData() {
      this.loading = true;
      try {
        // Calling the endpoint that returns both { stats, recent }
        const response = await api.get('/dashboard-stats');
        const { stats, recent } = response.data;

        // 1. Update the Card Values
        this.stats.forEach(stat => {
          if (stats[stat.key] !== undefined) {
            stat.value = stats[stat.key].toLocaleString();
          }
        });

        // 2. Update the Recent Records Table
        this.patients = recent;

      } catch (error) {
        console.error("Failed to load dashboard data:", error);
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    this.fetchDashboardData();
  }
}
</script>